// src/pages/Feedback.tsx
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { submitUserFeedback } from '../services/api/userFeedback';

const Feedback: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { showToast } = useToast();
  const [content, setContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // 自動調整 textarea 高度
  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = textarea.scrollHeight + 'px';
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [content]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      showToast({ type: 'error', title: 'Please write something before sending' });
      return;
    }

    if (!currentUser) {
      showToast({ type: 'error', title: 'Please login to send feedback' });
      return;
    }

    setIsSubmitting(true);
    
    try {
      await submitUserFeedback({
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        content: content.trim(),
        context: {
          currentPage: '/feedback',
          userAgent: navigator.userAgent
        }
      });

      showToast({ type: 'success', title: 'Thank you for your feedback! We\'ll review every message.' });
      navigate('/profile');
      
    } catch (error: unknown) {
      console.error('Submit feedback failed:', error);
      
      const errorMessage = error instanceof Error ? error.message : String(error);
      
      if (errorMessage.includes('rate limit')) {
        showToast({ type: 'error', title: 'Please wait 5 minutes before sending another feedback' });
      } else {
        showToast({ type: 'error', title: 'Failed to send feedback. Please try again.' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    navigate('/profile');
  };

  return (
    <div className="h-full relative pb-20 md:pb-0">
      <div className="content-loose">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center mb-2">
            <button
              onClick={handleBack}
              className="p-2 -ml-2 rounded-lg hover:bg-neutral-100 transition-colors mr-2"
              aria-label="Go back"
            >
              <ArrowLeft size={20} className="text-neutral-600" />
            </button>
            <h1 className="text-xl font-semibold text-neutral-900">
              Tell us anything
            </h1>
          </div>
          <p className="text-neutral-600 text-sm md:text-base leading-relaxed ml-10">
            Found a bug? Have an idea? Any feedback is welcome. We read every message.
          </p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 overflow-hidden">
          <form onSubmit={handleSubmit} className="flex flex-col">
            
            {/* Textarea - 主要輸入區域 */}
            <div className="p-6 md:p-8">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Found a bug? Have an idea? Any feedback is welcome..."
                className="
                  w-full resize-none
                  p-0 border-none outline-none
                  text-neutral-900 placeholder-neutral-400
                  text-base leading-relaxed
                  min-h-[320px] md:min-h-[400px]
                  bg-transparent
                "
                disabled={isSubmitting}
              />
            </div>

            {/* Bottom Section - 固定在底部 */}
            <div className="px-6 md:px-8 py-4 bg-neutral-50 border-t border-neutral-100">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-sm text-neutral-500 flex items-center gap-3 order-2 md:order-1">
                  {content.length > 1000 && (
                    <span className="text-warning-600 text-xs">
                      Consider shortening your message
                    </span>
                  )}
                  <span className="text-xs">{content.length} characters</span>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !content.trim()}
                  className="
                    w-full md:w-auto md:min-w-[160px] py-2.5 px-6 
                    bg-sky-500 hover:bg-sky-600 disabled:bg-neutral-300
                    text-white font-medium rounded-lg
                    flex items-center justify-center gap-2
                    transition-colors duration-200
                    disabled:cursor-not-allowed
                    text-sm
                    min-h-[44px]
                    order-1 md:order-2
                    whitespace-nowrap
                  "
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      <span>Send Feedback</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Feedback;